<!--
  Component: MetricVizCompBars

  This component uses VizCompBars to create an svg visualization for a bar
  graph with multiple groups of bars to compare main and comp value.  Mainly
  serves as a wrapper to process metadata and data to pass to VizCompBars.

  Metadata Notes:
  - Optional legend describing comp bar's type (hidden if not available)
    - To have both main + comp legend text, use both _main and _comp versions
  - Has underlying variable names for main and comparison bar values
  - Number display format (for main value), to be shown to the right of bars
  - Max range of values for scaling of bars
  - Number of bar groups is determined automatically
    - Based on the Main Value slots
  - Number of lines in label for each bar group is determined automatically
    - n value for group is shown in last row
  - Variable for n value, will be shown in parenthesis of group label (left of
    axis)
  - Variables for main bar n and comp bar ns, will be shown in parenthesis
    after bar value labels (right of bars)
  - CSS formats for main and comp bars
-->

<template>
  <div class="metric-viz-comp-bars" :class="cssClass">
    <VizLegendDivs
      :legend="legendText"
      :main-css-class="mainCss"
      :comp-css-classes="compCss"
      :aria-label-text="ariaLabelLegend"
    />
    <VizCompBars
      :aria-label-text="ariaLabel"
      :bar-data="barData"
      :row-label-width="rowLabelWidth"
      :val-label-width="valLabelWidth"
      :row-padding-bottom="rowPaddingBottom"
      :range-max="barMax"
      :val-format="valFormat"
      :equal-bar-heights="equalBarHeights"
      :label-greater-and-less-than-percent="labelGreaterAndLessThanPercent"
      :main-css-class="mainCss"
      :comp-css-classes="compCss"
      :is-full-width="true"
      :comparison-hidden="isCovidYear"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Component, { mixins } from 'vue-class-component'

import { MetadataDescMixin, MetadataAriaLabelMixin } from './metadata-mixin'

import VizCompBars from '@/components/VizCompBars'

import VizLegendDivs from '@/components/VizLegendDivs'

import { NS_CURRENT_REPORT } from '../const'

/* Sizing constants */
const ROW_LABEL_WIDTH_BASE = 170
const ROW_LABEL_WIDTH_INFO = 120
const VAL_LABEL_WIDTH_INFO = 85
const ROW_PADDING_BOTTOM_SURVEY = 0
const ROW_LABEL_WIDTH_SURVEY = 255

/* Prefix (followed by number) for Valid Slot Names for this Component */
const SN_MAIN_N_PREFIX = 'main_n_'
const SN_MAIN_VAL_PREFIX = 'main_val_'
const SN_COMP_VAL_PREFIX = 'comp_val_'
const SN_LABEL_PREFIX = 'label_'
const SN_LEGEND_PREFIX = 'legend_text' // index not required
const SN_LEGEND_MAIN_PREFIX = 'legend_text_main' // index not required
const SN_LEGEND_COMP_PREFIX = 'legend_text_comp_' // requires 2 indexes
const SN_COMP_CSS_CLASSES_PREFIX = 'comp_css_'
const SN_MAIN_BAR_N_PREFIX = 'main_bar_n_'
const SN_COMP_BAR_N_PREFIX = 'comp_bar_n_'

/* Valid Slot Names for this Component */
const SN_VAL_FMT = 'val_disp_fmt'
const SN_BAR_MAX = 'bar_range_max'
const SN_MAIN_CSS_CLASS = 'main_css'

@Component({
  computed: {
    ...mapGetters(NS_CURRENT_REPORT, [
      'isCovidYear'
    ])
  },
  components: {
    VizCompBars,
    VizLegendDivs
  }
})
export default class MetricVizCompBars extends mixins(MetadataDescMixin, MetadataAriaLabelMixin) {
  cssClass = null
  rowLabelWidth = ROW_LABEL_WIDTH_BASE
  valLabelWidth = null
  equalBarHeights = false
  rowPaddingBottom = null
  labelGreaterAndLessThanPercent = false

  /* Getters */

  // Metadata info
  get legendText () {
    const mainData = this.slotDataForPrefix(SN_LEGEND_MAIN_PREFIX)
    const compData = this.slotDataForPrefix(SN_LEGEND_COMP_PREFIX)

    if (mainData.length > 0 || compData.length > 0) {
      let compArr = []
      compData.forEach(c => {
        // This assumes all comp data slot names have exactly 2 indexes
        // Group comp bar legend data by only first index
        const compGroupIndex = +c.component_slot.split('_').slice(-2)[0]
        compArr[compGroupIndex] = compArr[compGroupIndex] || []
        compArr[compGroupIndex].push(this.descReplaceDisplayTextForSlot(c))
      })
      compArr = compArr.filter(f => f !== undefined)

      return {
        main: mainData.map(this.descReplaceDisplayTextForSlot),
        comp: compArr
      }
    } else {
      const data = this.slotDataForPrefix(SN_LEGEND_PREFIX)
      return data.map(this.descReplaceDisplayTextForSlot)
    }
  }

  get mainCss () { return this.displayTextForSlotName(SN_MAIN_CSS_CLASS) }
  get compCss () {
    // NOTE: These classes are not matched based on indexes to the bars in
    //   legend or chart - they will just be displayed in the order provided.
    //   In order to match, we would have to parse the indexes both here and
    //   in comp bar data.
    const s = this.slotDataForPrefix(SN_COMP_CSS_CLASSES_PREFIX)
    return s.map(this.displayTextForSlot)
  }

  get valFormat () { return this.displayTextForSlotName(SN_VAL_FMT) }

  get barMax () {
    const m = +this.displayTextForSlotName(SN_BAR_MAX)
    if (m <= 0) {
      console.log(`Error: displayTextForSlotName ${SN_BAR_MAX} not valid: ${m}`)
    }
    return m
  }

  get mainValSlotData () { return this.slotDataForPrefix(SN_MAIN_VAL_PREFIX) }

  get barData () {
    const mainData = this.mainValSlotData

    return mainData.map(m => {
      return {
        rowLabel: this.labelTextLinesForMainSlot(m),
        nVal: this.nDispValueForMainSlot(m),
        mainN: this.mainNForMainSlot(m),
        compN: this.compNsForMainSlot(m),
        mainVal: this.mainValueForMainSlot(m),
        compVal: this.compValuesForMainSlot(m)
      }
    })
  }

  /* Methods - utility */

  // Assume all other slots relative to Main Value slot
  slotNameForMainSlot (slotData, newPrefix) {
    return slotData.component_slot.replace(SN_MAIN_VAL_PREFIX, newPrefix)
  }
  nSlotNameForMainSlot (slotData) {
    return this.slotNameForMainSlot(slotData, SN_MAIN_N_PREFIX)
  }
  compValSlotNameForMainSlot (slotData) {
    return this.slotNameForMainSlot(slotData, SN_COMP_VAL_PREFIX)
  }
  mainNSlotNameForMainSlot (slotData) {
    return this.slotNameForMainSlot(slotData, SN_MAIN_BAR_N_PREFIX)
  }
  compNSlotNameForMainSlot (slotData) {
    return this.slotNameForMainSlot(slotData, SN_COMP_BAR_N_PREFIX)
  }

  // Raw values used for calculating bar lengths and display label
  mainValueForMainSlot (slotData) {
    return this.valueForSlot(slotData)
  }
  compValuesForMainSlot (slotData) {
    // NOTE: These values are not matched based on indexes to the n or css
    const compName = this.compValSlotNameForMainSlot(slotData)
    return this.slotDataForPrefix(compName).map(this.valueForSlot)
  }

  // String value for n in parenthesis, may be e.g. N/A or n<5
  nDispValueForMainSlot (slotData) {
    const nName = this.nSlotNameForMainSlot(slotData)
    return this.dispValueForSlotName(nName)
  }
  mainNForMainSlot (slotData) {
    const nName = this.mainNSlotNameForMainSlot(slotData)
    return this.dispValueForSlotName(nName)
  }
  compNsForMainSlot (slotData) {
    // NOTE: These ns are not matched based on indexes to the vals or css
    const nName = this.compNSlotNameForMainSlot(slotData)
    return this.slotDataForPrefix(nName).map(this.valueForSlot)
  }

  labelSlotBaseForMainSlot (slotData) {
    return this.slotNameForMainSlot(slotData, SN_LABEL_PREFIX)
  }
  labelSlotDataForMainSlot (slotData) {
    const labelBase = this.labelSlotBaseForMainSlot(slotData)
    return this.slotDataForPrefix(labelBase)
  }
  // May have multiple lines of text per label
  labelTextLinesForMainSlot (slotData) {
    const labelData = this.labelSlotDataForMainSlot(slotData)
    return labelData.map(this.descReplaceDisplayTextForSlot)
  }
}

class MetricVizCompBarsInfo extends MetricVizCompBars {
  cssClass = 'info'
  rowLabelWidth = ROW_LABEL_WIDTH_INFO
  valLabelWidth = VAL_LABEL_WIDTH_INFO
  equalBarHeights = true
  labelGreaterAndLessThanPercent = true
}

class MetricVizCompBarsSurvey extends MetricVizCompBars {
  cssClass = 'survey'
  rowLabelWidth = ROW_LABEL_WIDTH_SURVEY
  rowPaddingBottom = ROW_PADDING_BOTTOM_SURVEY

  get barData () {
    const mainData = this.mainValSlotData

    return mainData.map(m => {
      return {
        rowLabel: this.labelTextLinesForMainSlot(m),
        nVal: this.nDispValueForMainSlot(m),
        mainN: this.mainNForMainSlot(m),
        compN: this.compNsForMainSlot(m),
        mainVal: this.mainValueForMainSlot(m),
        compVal: this.compValuesForMainSlot(m),
        valLabel: this.nDispValueForMainSlot(m) === '0' ? '' : undefined
      }
    })
  }
}

export { MetricVizCompBars, MetricVizCompBarsInfo, MetricVizCompBarsSurvey }

</script>

<!-- These are base-version formatting rules meant to be overridden, so keep them unscoped -->
<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.viz-comp-bars {
  padding: 6px 0px;
}
.metric-viz-comp-bars {
  &:not(.info) .viz-legend {
    /deep/ .legend-group {
      color: #555555;
    }
  }
  &.survey {
    /deep/ .bar-group:not(:nth-child(1)) .bar-group-labels {
      fill: #555;
    }
    /deep/ .bar-group:not(:nth-child(1)) .bar-group-item text{
      fill: #555;
    }
    /deep/ .bar-group:not(:nth-child(1)) .bar.main {
      fill: #949494;
    }

    margin-bottom: 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/sqs/2022/tab-mods/MetricVizCompBars.vue